import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import {
  formatCurrency,
  formatNumber,
  formatNumberOrUnknown,
  formatPercentage,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import MetricsTimelineChart, {
  type MetricDataPoint,
} from "../card/MetricsTimelineChart";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import {
  useGetMergedInsertionOrderMetricsQuery,
  useGetMergedLineItemMetricsQuery,
} from "../../store/apis/internalCampaignsMergeApi";
import LineItemsTable from "../table/LineItemsTable";
import LineItemMetricsChart from "../chart/LineItemMetricsChart";
import InsertionOrdersTable from "../table/InsertionOrdersTable";
import InsertionOrderMetricsChart from "../chart/InsertionOrderMetricsChart";
import DV360Cards from "../card/DV360Cards";

import CreativeMetricsSection from "./CreativeMetricsSection";
import DimensionMetricsSection from "./DimensionMetricsSection";
import PublisherMetricsSection from "./PublisherMetricsSection";

interface Dv360MergeCampaignDetailsProps {
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
  metricsStartDate?: string;
  metricsEndDate?: string;
  onMetricsStartDateChange?: (value: string) => void;
  onMetricsEndDateChange?: (value: string) => void;
  onClearMetricsDateFilters?: () => void;
}

const Dv360MergeCampaignDetails: React.FC<Dv360MergeCampaignDetailsProps> = ({
  isLoadingMergedCampaign,
  mergedCampaignId,
  mergedCampaignData,
  metricsStartDate,
  metricsEndDate,
  onMetricsStartDateChange,
  onMetricsEndDateChange,
  onClearMetricsDateFilters,
}) => {
  const getChartDataPoints = (
    dailyData: components["schemas"]["DailyMetrics"][] = []
  ): MetricDataPoint[] =>
    dailyData.map((day) => ({
      date: day.date,
      ctr: day.ctr ?? 0,
      cpc: day.cpc ?? 0,
      cpm: day.cpm ?? 0,
      impressions: day.impressions ?? 0,
      clicks: day.clicks ?? 0,
      cost: day.cost ?? 0,
      completeViews: day.completeViews ?? 0,
    }));

  const format: "DISPLAY" | "NATIVE" | "VIDEO" | "ALL" | undefined =
    mergedCampaignData?.format || "ALL";
  // El desglose por line item (AdLine) solo se enseña en YouTube. En display, Feel
  // IABA y el resto de DV360 cada IO lleva un único line item que repite la IO.
  const isYoutube = mergedCampaignData?.isYoutube ?? false;
  const [internalStartDate, setInternalStartDate] = useState<string>("");
  const [internalEndDate, setInternalEndDate] = useState<string>("");
  const [lineItemStartDate, setLineItemStartDate] = useState<string>("");
  const [lineItemEndDate, setLineItemEndDate] = useState<string>("");
  const [selectedLineItem, setSelectedLineItem] = useState<{
    id?: string;
    name?: string;
    dailyData: components["schemas"]["DailyLineItemMetrics"][];
    summary: components["schemas"]["MetricsLineItemSummary"];
  } | null>(null);

  const [insertionOrderStartDate, setInsertionOrderStartDate] =
    useState<string>("");
  const [insertionOrderEndDate, setInsertionOrderEndDate] =
    useState<string>("");
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<{
    id?: string;
    name?: string;
    dailyData: components["schemas"]["DailyLineItemMetrics"][];
    summary: components["schemas"]["MetricsLineItemSummary"];
  } | null>(null);

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    data: insertionOrderMetrics,
    isLoading: isLoadingInsertionOrderMetrics,
    error: insertionOrderMetricsError,
  } = useGetMergedInsertionOrderMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      startDate: insertionOrderStartDate,
      endDate: insertionOrderEndDate,
    },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const {
    data: lineItemMetrics,
    isLoading: isLoadingLineItemMetrics,
    error: lineItemMetricsError,
  } = useGetMergedLineItemMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      startDate: lineItemStartDate,
      endDate: lineItemEndDate,
    },
    {
      skip: !mergedCampaignId || !isYoutube,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const formatDateForInput = useCallback(
    (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    },
    []
  );

  const resolvedStartDate = metricsStartDate ?? internalStartDate;
  const resolvedEndDate = metricsEndDate ?? internalEndDate;

  const handleMetricsStartDateChange = useCallback(
    (value: string) => {
      if (onMetricsStartDateChange) {
        onMetricsStartDateChange(value);
      } else {
        setInternalStartDate(value);
      }

      if (!value) {
        if (onMetricsEndDateChange) {
          onMetricsEndDateChange("");
        } else {
          setInternalEndDate("");
        }
      }
    },
    [onMetricsStartDateChange, onMetricsEndDateChange]
  );

  const handleMetricsEndDateChange = useCallback(
    (value: string) => {
      if (onMetricsEndDateChange) {
        onMetricsEndDateChange(value);
      } else {
        setInternalEndDate(value);
      }
    },
    [onMetricsEndDateChange]
  );

  const handleClearMetricsDates = useCallback(() => {
    if (onClearMetricsDateFilters) {
      onClearMetricsDateFilters();
    } else {
      setInternalStartDate("");
      setInternalEndDate("");
    }
  }, [onClearMetricsDateFilters]);

  // Initialize date fields with campaign dates when data loads
  useEffect(() => {
    if (mergedCampaignData?.startDate && !resolvedStartDate) {
      handleMetricsStartDateChange(
        formatDateForInput(mergedCampaignData.startDate)
      );
    }
    if (mergedCampaignData?.endDate && !resolvedEndDate) {
      handleMetricsEndDateChange(
        formatDateForInput(mergedCampaignData.endDate)
      );
    }
  }, [
    mergedCampaignData,
    formatDateForInput,
    resolvedStartDate,
    resolvedEndDate,
    handleMetricsStartDateChange,
    handleMetricsEndDateChange,
  ]);

  // Initialize date fields with campaign dates when data loads
  useEffect(() => {
    if (mergedCampaignData?.startDate && !lineItemStartDate) {
      setLineItemStartDate(formatDateForInput(mergedCampaignData.startDate));
    }
    if (mergedCampaignData?.endDate && !lineItemEndDate) {
      setLineItemEndDate(formatDateForInput(mergedCampaignData.endDate));
    }
    if (mergedCampaignData?.startDate && !insertionOrderStartDate) {
      setInsertionOrderStartDate(
        formatDateForInput(mergedCampaignData.startDate)
      );
    }
    if (mergedCampaignData?.endDate && !insertionOrderEndDate) {
      setInsertionOrderEndDate(formatDateForInput(mergedCampaignData.endDate));
    }
  }, [
    mergedCampaignData,
    formatDateForInput,
    lineItemStartDate,
    lineItemEndDate,
    insertionOrderStartDate,
    insertionOrderEndDate,
  ]);

  // Set the first insertion order as selected when data loads
  useEffect(() => {
    if (
      insertionOrderMetrics?.insertionOrders &&
      insertionOrderMetrics.insertionOrders.length > 0 &&
      !selectedInsertionOrder
    ) {
      setSelectedInsertionOrder(insertionOrderMetrics.insertionOrders[0]);
    }
  }, [insertionOrderMetrics, selectedInsertionOrder]);

  // Set the first line item as selected when data loads
  useEffect(() => {
    if (
      lineItemMetrics?.lineItems &&
      lineItemMetrics.lineItems.length > 0 &&
      !selectedLineItem
    ) {
      setSelectedLineItem(lineItemMetrics.lineItems[0]);
    }
  }, [lineItemMetrics, selectedLineItem]);

  useEffect(() => {
    if (lineItemMetricsError || insertionOrderMetricsError) {
      dispatch(
        setToastMessage({
          message: t("common.errorFetchingData", {
            defaultValue: "Error fetching data",
          }),
          severity: "error",
        })
      );
    }
  }, [lineItemMetricsError, insertionOrderMetricsError, dispatch, t]);

  const handleInsertionOrderClick = useCallback(
    (insertionOrder: {
      id?: string;
      name?: string;
      dailyData: components["schemas"]["DailyLineItemMetrics"][];
      summary: components["schemas"]["MetricsLineItemSummary"];
    }) => {
      setSelectedInsertionOrder(insertionOrder);
    },
    []
  );

  const handleLineItemClick = useCallback(
    (lineItem: {
      id?: string;
      name?: string;
      dailyData: components["schemas"]["DailyLineItemMetrics"][];
      summary: components["schemas"]["MetricsLineItemSummary"];
    }) => {
      setSelectedLineItem(lineItem);
    },
    []
  );

  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";

  const handleExportInsertionOrderMetrics = useCallback(() => {
    if (!insertionOrderMetrics?.insertionOrders) {
      return;
    }

    const csvData = [];

    // Add header row based on format
    const headers = [t("campaigns.name", { defaultValue: "Name" })];

    if (format === "VIDEO") {
      // VIDEO format headers
      headers.push(
        t("campaigns.investment", { defaultValue: "Investment" }),
        t("campaigns.visualizations", { defaultValue: "Visualizations" }),
        t("campaigns.cpv", { defaultValue: "CPV" }),
        t("campaigns.impressions", { defaultValue: "Impressions" }),
        t("campaigns.cpm", { defaultValue: "CPM" }),
        t("campaigns.vtr", { defaultValue: "VTR" }),
        t("campaigns.firstQuartile", { defaultValue: "First Quartile" }),
        t("campaigns.secondQuartile", { defaultValue: "Second Quartile" }),
        t("campaigns.thirdQuartile", { defaultValue: "Third Quartile" }),
        t("campaigns.uniqueUsers", { defaultValue: "Unique Users" }),
        t("campaigns.completeViews", { defaultValue: "Complete Views" }),
        t("campaigns.measurableImpressions", {
          defaultValue: "Measurable Impressions",
        }),
        t("campaigns.engagementRate", { defaultValue: "Engagement Rate" }),
        t("campaigns.interactionTime", { defaultValue: "Interaction Time" }),
        t("campaigns.totalReach", { defaultValue: "Total Reach" })
      );
    } else if (format === "DISPLAY") {
      // DISPLAY format headers
      headers.push(
        t("campaigns.investment", { defaultValue: "Investment" }),
        t("campaigns.impressions", { defaultValue: "Impressions" }),
        t("campaigns.clicks", { defaultValue: "Clicks" }),
        t("campaigns.cpm", { defaultValue: "CPM" }),
        t("campaigns.cpc", { defaultValue: "CPC" }),
        t("campaigns.ctr", { defaultValue: "CTR" }),
        t("campaigns.conversionRate", { defaultValue: "Conversion Rate" }),
        t("campaigns.totalConversions", { defaultValue: "Conversions" }),
        t("campaigns.cpa", { defaultValue: "CPA" }),
        t("campaigns.measurableImpressions", {
          defaultValue: "Measurable Impressions",
        }),
        t("campaigns.engagementRate", { defaultValue: "Engagement Rate" }),
        t("campaigns.interactionTime", { defaultValue: "Interaction Time" }),
        t("campaigns.totalReach", { defaultValue: "Total Reach" })
      );
    } else if (format === "NATIVE") {
      // NATIVE format headers
      headers.push(
        t("campaigns.investment", { defaultValue: "Investment" }),
        t("campaigns.impressions", { defaultValue: "Impressions" }),
        t("campaigns.clicks", { defaultValue: "Clicks" }),
        t("campaigns.cpc", { defaultValue: "CPC" }),
        t("campaigns.ctr", { defaultValue: "CTR" }),
        t("campaigns.uniqueUsers", { defaultValue: "Unique Users" }),
        t("campaigns.measurableImpressions", {
          defaultValue: "Measurable Impressions",
        }),
        t("campaigns.engagementRate", { defaultValue: "Engagement Rate" }),
        t("campaigns.interactionTime", { defaultValue: "Interaction Time" }),
        t("campaigns.totalReach", { defaultValue: "Total Reach" })
      );
    } else {
      // ALL format headers
      headers.push(
        t("campaigns.investment", { defaultValue: "Investment" }),
        t("campaigns.impressions", { defaultValue: "Impressions" }),
        t("campaigns.clicks", { defaultValue: "Clicks" }),
        t("campaigns.ctr", { defaultValue: "CTR" }),
        t("campaigns.totalConversions", { defaultValue: "Conversions" }),
        t("campaigns.vtr", { defaultValue: "VTR" }),
        t("campaigns.measurableImpressions", {
          defaultValue: "Measurable Impressions",
        }),
        t("campaigns.engagementRate", { defaultValue: "Engagement Rate" }),
        t("campaigns.interactionTime", { defaultValue: "Interaction Time" }),
        t("campaigns.totalReach", { defaultValue: "Total Reach" })
      );
    }

    // Add Real Cost header only for admin users
    if (isAdmin) {
      headers.push(t("campaigns.realCost", { defaultValue: "Real Cost" }));
    }

    csvData.push(headers);

    // Add data rows based on format
    insertionOrderMetrics.insertionOrders.forEach((insertionOrder) => {
      const row = [insertionOrder.name || "Unnamed AdOrder"];

      if (format === "VIDEO") {
        // VIDEO format data - using available fields only
        row.push(
          formatCurrency(
            insertionOrder.summary.totalCost,
            mergedCampaignData?.currency_enum
          ), // Investment
          formatNumber(0), // Visualizations - not available in insertion order summary yet
          formatCurrency(0, mergedCampaignData?.currency_enum), // CPV - not available in insertion order summary yet
          insertionOrder.summary.totalImpressions.toString(), // Impressions
          formatCurrency(
            insertionOrder.summary.avgCPM,
            mergedCampaignData?.currency_enum
          ), // CPM
          formatPercentage(insertionOrder.summary.avgVTR || 0), // VTR
          formatNumber(insertionOrder.summary.totalFirstQuartileViews || 0), // First Quartile Views
          formatNumber(insertionOrder.summary.totalMidpointViews || 0), // Second Quartile Views
          formatNumber(insertionOrder.summary.totalThirdQuartileViews || 0), // Third Quartile Views
          formatNumberOrUnknown(insertionOrder.summary.totalUniqueUsers, ""), // Unique Users
          formatNumber(insertionOrder.summary.totalCompleteViews || 0), // Complete Views
          formatNumber(insertionOrder.summary.totalMeasurableImpressions || 0), // Measurable Impressions
          formatPercentage(insertionOrder.summary.avgEngagementRate || 0), // Engagement Rate
          formatNumber(insertionOrder.summary.avgInteractionTime || 0), // Interaction Time
          formatNumberOrUnknown(insertionOrder.summary.totalReachCoViewed, "") // Total Reach
        );
      } else if (format === "DISPLAY") {
        // DISPLAY format data
        row.push(
          formatCurrency(
            insertionOrder.summary.totalCost,
            mergedCampaignData?.currency_enum
          ), // Investment
          insertionOrder.summary.totalImpressions.toString(), // Impressions
          insertionOrder.summary.totalClicks.toString(), // Clicks
          formatCurrency(
            insertionOrder.summary.avgCPM,
            mergedCampaignData?.currency_enum
          ), // CPM
          formatCurrency(
            insertionOrder.summary.avgCPC,
            mergedCampaignData?.currency_enum
          ), // CPC
          formatPercentageNoMultiply(insertionOrder.summary.avgCTR), // CTR
          formatPercentage(0), // Conversion Rate - not available
          formatNumber(insertionOrder.summary.totalConversions || 0), // Conversions
          formatCurrency(0, mergedCampaignData?.currency_enum), // CPA - not available
          formatNumber(insertionOrder.summary.totalMeasurableImpressions || 0), // Measurable Impressions
          formatPercentage(insertionOrder.summary.avgEngagementRate || 0), // Engagement Rate
          formatNumber(insertionOrder.summary.avgInteractionTime || 0), // Interaction Time
          formatNumberOrUnknown(insertionOrder.summary.totalReachCoViewed, "") // Total Reach
        );
      } else if (format === "NATIVE") {
        // NATIVE format data
        row.push(
          formatCurrency(
            insertionOrder.summary.totalCost,
            mergedCampaignData?.currency_enum
          ), // Investment
          insertionOrder.summary.totalImpressions.toString(), // Impressions
          insertionOrder.summary.totalClicks.toString(), // Clicks
          formatCurrency(
            insertionOrder.summary.avgCPC,
            mergedCampaignData?.currency_enum
          ), // CPC
          formatPercentageNoMultiply(insertionOrder.summary.avgCTR), // CTR
          formatNumberOrUnknown(insertionOrder.summary.totalUniqueUsers, ""), // Unique Users
          formatNumber(insertionOrder.summary.totalMeasurableImpressions || 0), // Measurable Impressions
          formatPercentage(insertionOrder.summary.avgEngagementRate || 0), // Engagement Rate
          formatNumber(insertionOrder.summary.avgInteractionTime || 0), // Interaction Time
          formatNumberOrUnknown(insertionOrder.summary.totalReachCoViewed, "") // Total Reach
        );
      } else {
        // ALL format data
        row.push(
          formatCurrency(
            insertionOrder.summary.totalCost,
            mergedCampaignData?.currency_enum
          ), // Investment
          insertionOrder.summary.totalImpressions.toString(), // Impressions
          insertionOrder.summary.totalClicks.toString(), // Clicks
          formatPercentageNoMultiply(insertionOrder.summary.avgCTR), // CTR
          formatNumber(insertionOrder.summary.totalConversions || 0), // Conversions
          formatPercentage(insertionOrder.summary.avgVTR || 0), // VTR
          formatNumber(insertionOrder.summary.totalMeasurableImpressions || 0), // Measurable Impressions
          formatPercentage(insertionOrder.summary.avgEngagementRate || 0), // Engagement Rate
          formatNumber(insertionOrder.summary.avgInteractionTime || 0), // Interaction Time
          formatNumberOrUnknown(insertionOrder.summary.totalReachCoViewed, "") // Total Reach
        );
      }

      // Add Real Cost data only for admin users
      if (isAdmin && insertionOrder.summary.totalRealCost !== undefined) {
        row.push(
          formatCurrency(
            insertionOrder.summary.totalRealCost,
            mergedCampaignData?.currency_enum
          )
        );
      }

      csvData.push(row);
    });

    // Generate CSV and download
    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([BOM, csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `ad_orders_${mergedCampaignId}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [
    insertionOrderMetrics,
    mergedCampaignData,
    isAdmin,
    t,
    mergedCampaignId,
    format,
  ]);

  const handleExportLineItemMetrics = useCallback(() => {
    if (!lineItemMetrics?.lineItems) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    try {
      // Create flattened data structure for CSV
      const flattenedData = lineItemMetrics.lineItems.flatMap((lineItem) => {
        return lineItem.dailyData.map((daily) => {
          const baseData = {
            date: daily.date,
            lineItemName: lineItem.name || "Unnamed AdLine",
            lineItemId: lineItem.id || "",
            impressions: daily.impressions,
            clicks: daily.clicks,
            ctr: daily.ctr,
            cpm: daily.cpm,
            cpc: daily.cpc,
            cost: daily.cost,
          };

          // Only include realCost for admin users
          return isAdmin ? { ...baseData, realCost: daily.realCost } : baseData;
        });
      });

      // Create CSV header
      const headers = [
        "Date",
        "AdLine Name",
        "AdLine ID",
        "Impressions",
        "Clicks",
        "CTR",
        "CPM",
        "CPC",
        "Cost",
      ];

      // Add Real Cost header only for admin users
      if (isAdmin) {
        headers.push("Real Cost");
      }

      // Create CSV rows
      const rows = flattenedData.map((item) => {
        const row = [
          item.date,
          item.lineItemName,
          item.lineItemId,
          item.impressions.toString(),
          item.clicks.toString(),
          item.ctr.toString(),
          item.cpm.toString(),
          item.cpc.toString(),
          item.cost.toString(),
        ];

        // Add Real Cost value only for admin users
        if (isAdmin && "realCost" in item) {
          row.push(item.realCost?.toString() || "");
        }

        return row;
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");

      // Create a Blob with the CSV data
      const BOM = new Uint8Array([0xef, 0xbb, 0xbf]); // UTF-8 BOM for Excel compatibility
      const blob = new Blob([BOM, csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      // Create a download link and trigger the download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `ad-line-metrics-${mergedCampaignId}_${
          new Date().toISOString().split("T")[0]
        }.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      dispatch(
        setToastMessage({
          message: t("common.exportSuccess", {
            defaultValue: "AdLine data exported successfully",
          }),
          severity: "success",
        })
      );
    } catch (error) {
      console.error("Error exporting line item metrics data:", error);

      dispatch(
        setToastMessage({
          message: t("campaigns.exportError", {
            defaultValue: "Error exporting AdLine metrics data",
          }),
          severity: "error",
        })
      );
    }
  }, [lineItemMetrics, mergedCampaignId, t, dispatch, isAdmin]);

  const handleExportMetrics = useCallback(() => {
    if (!mergedCampaignData?.metrics?.dailyData) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    // Transform the data based on format - only include metrics that are displayed in cards
    const formattedData = mergedCampaignData?.metrics?.dailyData.map((day) => {
      if (format === "VIDEO") {
        // VIDEO format metrics
        return {
          date: day.date,
          dailyInvestment: day.cost || 0,
          dailyImpressions: day.impressions || 0,
          impressions: day.impressions || 0,
          clicks: 0, // Not used
          ctr: 0, // Not used
          cpm: day.cpm || 0,
          cpc: 0, // Not used
          cost: day.cost || 0,
          cpv: day.cpv || 0,
          vtr: day.vtr || 0,
          dailyCTR: 0, // Not used
          conversions: 0, // Not used
          completeViews: day.completeViews || 0,
          currency: mergedCampaignData?.currency_enum || "USD",
          // Video-specific fields
          visualizations: day.visualizations || 0,
          firstQuartile: day.firstQuartile || 0,
          secondQuartile: day.secondQuartile || 0,
          thirdQuartile: day.thirdQuartile || 0,
          uniqueUsers: day.uniqueUsers || 0,
        };
      } else if (format === "DISPLAY") {
        // DISPLAY format metrics
        return {
          date: day.date,
          dailyInvestment: day.cost || 0,
          dailyImpressions: day.impressions || 0,
          impressions: day.impressions || 0,
          clicks: day.clicks || 0,
          ctr: day.ctr || 0,
          cpm: day.cpm || 0,
          cpc: day.cpc || 0,
          cost: day.cost || 0,
          cpv: 0, // Not used
          vtr: 0, // Not used
          dailyCTR: day.ctr || 0,
          conversions: day.conversions || 0,
          completeViews: 0, // Not used
          currency: mergedCampaignData?.currency_enum || "USD",
          conversionRate: day.conversionRate || 0,
          cpa: day.cpa || 0,
          uniqueUsers: 0, // Not used
        };
      } else if (format === "NATIVE") {
        // NATIVE format metrics
        return {
          date: day.date,
          dailyInvestment: day.cost || 0,
          dailyImpressions: day.impressions || 0,
          impressions: day.impressions || 0,
          clicks: day.clicks || 0,
          ctr: day.ctr || 0,
          cpm: 0, // Not used
          cpc: day.cpc || 0,
          cost: day.cost || 0,
          cpv: 0, // Not used
          vtr: 0, // Not used
          dailyCTR: day.ctr || 0,
          conversions: 0, // Not used
          completeViews: 0, // Not used
          currency: mergedCampaignData?.currency_enum || "USD",
          conversionRate: 0, // Not used
          cpa: 0, // Not used
          uniqueUsers: day.uniqueUsers || 0,
        };
      } else {
        // ALL format metrics
        return {
          date: day.date,
          dailyInvestment: day.cost || 0,
          dailyImpressions: day.impressions || 0,
          impressions: day.impressions || 0,
          clicks: day.clicks || 0,
          ctr: day.ctr || 0,
          cpm: 0, // Not used
          cpc: 0, // Not used
          cost: day.cost || 0,
          cpv: 0, // Not used
          vtr: day.vtr || 0,
          dailyCTR: day.ctr || 0,
          conversions: day.conversions || 0,
          completeViews: 0, // Not used
          currency: mergedCampaignData?.currency_enum || "USD",
          conversionRate: 0, // Not used
          cpa: 0, // Not used
          uniqueUsers: 0, // Not used
        };
      }
    });

    exportMetricsToCSV(
      formattedData,
      mergedCampaignData?.name || "export",
      t,
      dispatch
    );
  }, [mergedCampaignData, format, t, dispatch]);

  return (
    <>
      {isLoadingMergedCampaign ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Metrics */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("campaigns.metrics", { defaultValue: "Metrics" })}
              </Typography>

              {/* Date Filter */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  rowGap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label={t("common.startDate", {
                    defaultValue: "Start Date",
                  })}
                  type="date"
                  size="small"
                  value={resolvedStartDate || ""}
                  onChange={(e) => handleMetricsStartDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: formatDateForInput(mergedCampaignData?.startDate),
                    max:
                      resolvedEndDate ||
                      formatDateForInput(mergedCampaignData?.endDate),
                  }}
                  sx={{ width: 150 }}
                />

                <TextField
                  label={t("common.endDate", {
                    defaultValue: "End Date",
                  })}
                  type="date"
                  size="small"
                  value={resolvedEndDate || ""}
                  onChange={(e) => handleMetricsEndDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min:
                      resolvedStartDate ||
                      formatDateForInput(mergedCampaignData?.startDate),
                    max: formatDateForInput(mergedCampaignData?.endDate),
                  }}
                  sx={{ width: 150 }}
                  disabled={!resolvedStartDate}
                />

                <IconButton
                  onClick={handleClearMetricsDates}
                  size="small"
                  color="primary"
                  title={t("common.clearFilters", {
                    defaultValue: "Clear Filters",
                  })}
                >
                  <ClearIcon />
                </IconButton>

                {/* Export Button */}
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportMetrics}
                  sx={{
                    mt: { xs: 1, sm: 0 },
                    ml: { xs: 0, sm: 1 },
                    height: 36,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  {t("campaigns.exportMetrics", {
                    defaultValue: "Export Data",
                  })}
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <DV360Cards
              format={format}
              mergedCampaignData={mergedCampaignData}
            />

            {/* Metrics Timeline Chart */}
            {mergedCampaignData?.metrics?.dailyData &&
            mergedCampaignData.metrics.dailyData.length > 0 ? (
              <Box sx={{ mt: 4 }}>
                <MetricsTimelineChart
                  title={t("campaigns.metricsOverTime", {
                    defaultValue: "Metrics Over Time",
                  })}
                  subtitle={t("campaigns.clickMetricsToCompare", {
                    defaultValue:
                      "Click on metrics to compare or select 'All' to see accumulated data",
                  })}
                  data={getChartDataPoints(
                    mergedCampaignData.metrics.dailyData
                  )}
                  isLoading={isLoadingMergedCampaign}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  textAlign: "center",
                  bgcolor: theme.palette.background.default,
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t("campaigns.noMetricsData", {
                    defaultValue:
                      "No metrics data available for the selected time period. Data will appear here once the campaign starts generating metrics.",
                  })}
                </Typography>
              </Box>
            )}

            {/* AdOrder Section */}
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                mt: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("campaigns.insertionOrders", {
                    defaultValue: "AdOrder",
                  })}
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label={t("campaigns.startDate", {
                      defaultValue: "Start Date",
                    })}
                    type="date"
                    size="small"
                    value={insertionOrderStartDate}
                    onChange={(e) => setInsertionOrderStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />

                  <TextField
                    label={t("campaigns.endDate", {
                      defaultValue: "End Date",
                    })}
                    type="date"
                    size="small"
                    value={insertionOrderEndDate}
                    onChange={(e) => setInsertionOrderEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                    disabled={!insertionOrderStartDate}
                  />

                  <IconButton
                    onClick={() => {
                      setInsertionOrderStartDate("");
                      setInsertionOrderEndDate("");
                    }}
                    size="small"
                    color="primary"
                    title={t("common.clearFilters", {
                      defaultValue: "Clear Filters",
                    })}
                  >
                    <ClearIcon />
                  </IconButton>

                  {/* Export Button */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportInsertionOrderMetrics}
                    sx={{
                      mt: { xs: 1, sm: 0 },
                      ml: { xs: 0, sm: 1 },
                      height: 36,
                      textTransform: "none",
                      fontWeight: 500,
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      px: 1.5,
                    }}
                  >
                    {t("campaigns.exportInsertionOrders", {
                      defaultValue: "Export",
                    })}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* AdOrder Table */}
              {isLoadingInsertionOrderMetrics ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : insertionOrderMetrics?.insertionOrders &&
                insertionOrderMetrics.insertionOrders.length > 0 ? (
                <>
                  <InsertionOrdersTable
                    isLoading={isLoadingInsertionOrderMetrics}
                    insertionOrders={insertionOrderMetrics.insertionOrders}
                    currency={mergedCampaignData?.currency_enum || "USD"}
                    onInsertionOrderClick={handleInsertionOrderClick}
                    selectedInsertionOrderId={selectedInsertionOrder?.id}
                    format={format}
                  />

                  {/* Selected AdOrder Chart */}
                  {selectedInsertionOrder && (
                    <Box sx={{ mt: 3 }}>
                      <InsertionOrderMetricsChart
                        isLoading={isLoadingInsertionOrderMetrics}
                        dailyData={selectedInsertionOrder.dailyData}
                        title={selectedInsertionOrder.name || "Unnamed AdOrder"}
                        currency={mergedCampaignData?.currency_enum || "USD"}
                        isAdmin={isAdmin}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" sx={{ py: 2 }}>
                  {t("common.noData", {
                    defaultValue: "No insertion orders data available",
                  })}
                </Typography>
              )}
            </Paper>

            {/* AdLine Section: solo YouTube */}
            {isYoutube && (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  mt: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {t("campaigns.lineItems", {
                      defaultValue: "AdLine",
                    })}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      label={t("common.startDate", {
                        defaultValue: "Start Date",
                      })}
                      type="date"
                      size="small"
                      value={lineItemStartDate}
                      onChange={(e) => setLineItemStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: formatDateForInput(mergedCampaignData?.startDate),
                        max:
                          lineItemEndDate ||
                          formatDateForInput(mergedCampaignData?.endDate),
                      }}
                      sx={{ width: 150 }}
                    />
                    <TextField
                      label={t("common.endDate", {
                        defaultValue: "End Date",
                      })}
                      type="date"
                      size="small"
                      value={lineItemEndDate}
                      onChange={(e) => setLineItemEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: lineItemStartDate,
                        max: formatDateForInput(mergedCampaignData?.endDate),
                      }}
                      disabled={!lineItemStartDate}
                    />
                    <IconButton
                      onClick={() => {
                        setLineItemStartDate("");
                        setLineItemEndDate("");
                      }}
                      size="small"
                      color="primary"
                      title={t("common.clearFilters", {
                        defaultValue: "Clear Filters",
                      })}
                    >
                      <ClearIcon />
                    </IconButton>

                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportLineItemMetrics}
                      sx={{
                        ml: 1,
                        height: 36,
                        textTransform: "none",
                        fontWeight: 500,
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      disabled={
                        !lineItemMetrics?.lineItems ||
                        lineItemMetrics.lineItems.length === 0
                      }
                    >
                      {t("campaigns.exportLineItems", {
                        defaultValue: "Export AdLine",
                      })}
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Divider sx={{ mb: 2 }} />

                {/* AdLine Table */}
                {isLoadingLineItemMetrics ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : lineItemMetrics?.lineItems &&
                  lineItemMetrics.lineItems.length > 0 ? (
                  <>
                    <LineItemsTable
                      isLoading={isLoadingLineItemMetrics}
                      lineItems={lineItemMetrics.lineItems}
                      currency={mergedCampaignData?.currency_enum || "USD"}
                      onLineItemClick={handleLineItemClick}
                      selectedLineItemId={selectedLineItem?.id}
                      format={format}
                      ctrIsPreScaled
                    />

                    {/* Selected AdLine Chart */}
                    {selectedLineItem && (
                      <Box sx={{ mt: 3 }}>
                        <LineItemMetricsChart
                          isLoading={isLoadingLineItemMetrics}
                          dailyData={selectedLineItem.dailyData}
                          title={selectedLineItem.name || "Unnamed AdLine"}
                          currency={mergedCampaignData?.currency_enum || "USD"}
                          isAdmin={isAdmin}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" sx={{ py: 2 }}>
                    {t("common.noData", {
                      defaultValue: "No line items data available",
                    })}
                  </Typography>
                )}
              </Paper>
            )}

            {/*
              Desglose por creatividad. Va en todos los formatos: en display
              dice que pieza rinde, y en video es el desglose principal, el que
              sustituye al de dominios.
            */}
            <CreativeMetricsSection
              mergedCampaignId={mergedCampaignId}
              campaignName={mergedCampaignData?.name}
              currency={mergedCampaignData?.currency_enum}
              startDate={formatDateForInput(mergedCampaignData?.startDate)}
              endDate={formatDateForInput(mergedCampaignData?.endDate)}
            />

            {/*
              Desglose por site/dominio. DV360 lo puebla desde el report de
              FILTER_APP_URL, en la misma tabla que Outbrain y Taboola, asi que
              el componente es el mismo que usa el dashboard de NatAds.

              No se pinta en formato VIDEO: YouTube entrega dentro de un unico
              dominio, asi que la tabla saldria con una sola fila que no dice
              nada. Para video el desglose util es el de creatividad.
            */}
            {format !== "VIDEO" && (
              <PublisherMetricsSection
                mergedCampaignId={mergedCampaignId}
                campaignName={mergedCampaignData?.name}
                currency={mergedCampaignData?.currency_enum}
                startDate={formatDateForInput(mergedCampaignData?.startDate)}
                endDate={formatDateForInput(mergedCampaignData?.endDate)}
              />
            )}

            {/*
              Dispositivo y audiencia. Va en todos los formatos: el reparto por
              dispositivo es el insight de Connected TV en video, y en display
              dice desde donde se ve la campana.

              El componente se oculta solo si no hay ninguna de las dos, que es
              lo que pasa mientras el informe de audiencia siga sin ejecutarse.
            */}
            <DimensionMetricsSection
              mergedCampaignId={mergedCampaignId}
              currency={mergedCampaignData?.currency_enum}
            />
          </Paper>
        </>
      )}
    </>
  );
};

export default Dv360MergeCampaignDetails;
